'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import type { RatingCardConfig, RatingWidgetResolved } from './types';
import { Icon } from '@/components/Icon';
import { useSubstitute } from '@/lib/render/RenderContextProvider';
import {
  getSectionColorClass,
  type SectionBackgroundColor,
} from '@/lib/section-colors';

const STAR_REPEAT_PLACEHOLDER = '{location.googleAverageRating}';

function parseNumericValue(
  value: string,
): { iconCount: number; formatted: string } | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const num = parseFloat(trimmed);
  if (isNaN(num)) {
    return null;
  }
  return {
    iconCount: Math.floor(num),
    formatted: num.toLocaleString('de-DE', {
      minimumFractionDigits:
        trimmed.includes('.') || trimmed.includes(',') ? 1 : 0,
      maximumFractionDigits: 1,
    }),
  };
}

function CardIcons({
  card,
  sub,
}: {
  card: RatingCardConfig;
  sub: (s: string) => string;
}) {
  const isRating = card.value.includes(STAR_REPEAT_PLACEHOLDER);
  if (isRating) {
    const resolvedValue = sub(card.value);
    const numeric = parseNumericValue(resolvedValue);
    if (numeric && numeric.iconCount > 0) {
      return (
        <div className="mt-5 mb-2 flex gap-1">
          {Array.from({ length: numeric.iconCount }, (_, j) => (
            <Icon key={j} src={card.icon} size={40} className="text-muted" />
          ))}
        </div>
      );
    }
  }

  return <Icon src={card.icon} size={52} className="text-muted mt-4 mb-0" />;
}

export function RatingWidgetComponent({
  resolved,
}: {
  resolved: RatingWidgetResolved;
}) {
  const sub = useSubstitute();
  const cards = resolved.cards ?? [];
  const bgClass = getSectionColorClass(
    resolved.background as SectionBackgroundColor,
  );

  if (cards.length === 0) {
    return null;
  }

  return (
    <div className="pt-4">
      {/* Desktop: all cards with background, spacing between */}
      <div className="hidden gap-8 text-base md:grid md:grid-cols-3">
        {cards.map((card, i) => (
          <div
            key={i}
            className={classNames(
              'flex flex-col items-center justify-between gap-4 rounded-2xl p-8 text-center',
              bgClass,
            )}
          >
            <CardIcons card={card} sub={sub} />
            <p className="my-1">{sub(card.text)}</p>
            <p className="m-0 text-lg font-medium md:text-2xl">
              {sub(card.value)}
            </p>
          </div>
        ))}
      </div>

      {/* Mobile: stacked, first + last have bg, middle has no bg, no gap */}
      <div className="text-base md:hidden">
        {cards.map((card, i) => {
          const hasBg = i % 2 === 0;
          return (
            <div
              key={i}
              className={classNames(
                'flex flex-col items-center justify-between gap-4 text-center',
                hasBg ? classNames('rounded-2xl p-6', bgClass) : 'p-0',
              )}
            >
              <CardIcons card={card} sub={sub} />
              <p className="my-1">{sub(card.text)}</p>
              <p className="m-0 text-lg font-medium">{sub(card.value)}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
